import { yupResolver } from "@hookform/resolvers/yup";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import Grid from "@mui/material/Grid";

import ControlledTextFieldSeparatedLabel from "../../../components/controlled/ControlledTextFieldSeparatedLabel";

import { useAddGroupMutation } from "../recipientsApiSlice";

import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { getFormSchema } from "./formSchema";

export const AddDialog = ({ onClose, open, groups }) => {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [addGroup, { isLoading }] = useAddGroupMutation();

	const formSchema = getFormSchema(t);
	const {
		handleSubmit,
		control,
		formState: { errors },
		reset,
		setError,
	} = useForm({
		resolver: yupResolver(formSchema),
		shouldFocusError: true,
	});

	const submitNewUser = async ({ name }) => {
		if (Object.values(groups).some((group) => group === name)) {
			setError("name", {
				type: "custom",
				message: t("mailing_group_name_already_exists"),
			});
			return;
		}

		await addGroup({ name })
			.unwrap()
			.then(() => {
				enqueueSnackbar(t("added_mailing_group"), {
					variant: "success",
				});
			})
			.catch((error) => {
				if (error?.data?.errors) {
					const errors = error.data.errors;
					Object.entries(errors).forEach(([key, message]) => {
						setError(key);
						enqueueSnackbar(message[0], {
							variant: "error",
							preventDuplicate: true,
						});
					});
				} else {
					enqueueSnackbar(error?.data?.message || t("default_error"), {
						variant: "error",
						preventDuplicate: true,
					});
				}
			})
			.finally(() => {
				onClose();
			});
	};

	useEffect(() => {
		if (open) {
			reset();
		}
	}, [open, reset]);

	useEffect(() => {
		if (Object.keys(errors).length > 0) {
			Object.values(errors).forEach((err) => {
				enqueueSnackbar(err.message, {
					variant: "error",
					preventDuplicate: true,
				});
			});
		}
	}, [errors, enqueueSnackbar]);

	return (
		<Dialog fullWidth maxWidth="sm" onClose={onClose} open={open}>
			<Box sx={{ padding: "30px" }}>
				<Box component="form" noValidate onSubmit={handleSubmit(submitNewUser)}>
					<Grid container spacing={1.5}>
						<Grid size={12}>
							<ControlledTextFieldSeparatedLabel
								control={control}
								name="name"
								label={t("name_label")}
								error={!!errors?.name}
								required
							/>
						</Grid>
						<Grid size={12}>
							<Box sx={{ display: "flex", justifyContent: "center" }}>
								<Button loading={isLoading} type="submit" variant="contained">
									{t("add")}
								</Button>
							</Box>
						</Grid>
					</Grid>
				</Box>
			</Box>
		</Dialog>
	);
};
